import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Text,
  useColorModeValue,
  Spinner,
  Icon,
  VStack,
  HStack,
  Divider,
} from "@chakra-ui/react";
import { SearchIcon, AttachmentIcon, CloseIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { useContext, useState } from "react";
import { AppContext } from "AppProvider/AppContext";
import { appRequest } from "routes";
import { PDFDocument } from "pdf-lib";
import { motion, AnimatePresence } from "framer-motion";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const DOC_SLOTS = [
  { key: "invoice", label: "Invoice Copy", required: true, icon: AttachmentIcon },
  { key: "agreement", label: "Agreement Document", required: false, icon: AttachmentIcon },
  { key: "tax", label: "Tax Documents", required: false, icon: AttachmentIcon },
  { key: "other", label: "Additional Documents", required: false, icon: AttachmentIcon },
];

export default function DocumentUploadTab() {
  const { toastNotify } = useContext(AppContext);
  const [documentId, setDocumentId] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [docFound, setDocFound] = useState(false);
  const [loading, setLoading] = useState(false);

  const bgColor = useColorModeValue("rgba(255, 255, 255, 0.8)", "rgba(26, 32, 44, 0.8)");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const inputFocusBg = useColorModeValue("white", "gray.700");
  const slotBg = useColorModeValue("teal.50", "teal.900");
  const slotHoverBg = useColorModeValue("gray.50", "blackAlpha.200");
  const pageBg = useColorModeValue("linear(to-br, gray.50, teal.50)", "linear(to-br, gray.900, gray.800)");

  const glassStyles = {
    backdropFilter: "blur(12px)",
    border: "1px solid",
    borderColor: useColorModeValue("whiteAlpha.500", "whiteAlpha.200"),
    boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
  };

  const [fileSlots, setFileSlots] = useState<Record<string, File | null>>({
    invoice: null,
    agreement: null,
    tax: null,
    other: null,
  });

  const [previews, setPreviews] = useState<Record<string, string | null>>({
    invoice: null,
    agreement: null,
    tax: null,
    other: null,
  });

  const handleSearch = async () => {
    if (!documentId.trim()) {
      toastNotify({ title: "Error", message: "Please enter a Document ID", type: "error" });
      return;
    }

    try {
      setIsSearching(true);
      const response: any = await appRequest("document", "singledocument", { documentId: documentId.trim() });
      if (response?.status === "success") {
        setDocFound(true);
        toastNotify({ title: "Success", message: "Document found. You can now upload files.", type: "success" });
      } else {
        setDocFound(false);
        toastNotify({ title: "Error", message: response?.data || "Document not found", type: "error" });
      }
    } catch (error) {
      setDocFound(false);
      toastNotify({ title: "Error", message: "Failed to search document", type: "error" });
    } finally {
      setIsSearching(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileSlots((prev) => ({ ...prev, [key]: file }));
    setPreviews((prev) => ({ ...prev, [key]: URL.createObjectURL(file) }));
  };

  const removeFile = (key: string) => {
    if (previews[key]) URL.revokeObjectURL(previews[key]!);
    setFileSlots((prev) => ({ ...prev, [key]: null }));
    setPreviews((prev) => ({ ...prev, [key]: null }));
  };

  const mergeFiles = async (files: File[]): Promise<Uint8Array> => {
    const mergedPdf = await PDFDocument.create();
    for (const file of files) {
      if (file.type !== "application/pdf") continue;
      const bytes = await file.arrayBuffer();
      const pdf = await PDFDocument.load(bytes);
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((p) => mergedPdf.addPage(p));
    }
    return await mergedPdf.save();
  };

  const arrayBufferToBase64 = (buffer: Uint8Array | ArrayBuffer) => {
    let binary = "";
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const handleSubmit = async () => {
    if (!docFound) {
      toastNotify({ title: "Error", message: "Please find a valid document first", type: "error" });
      return;
    }

    const filesToMerge = DOC_SLOTS.map(s => fileSlots[s.key]).filter((f): f is File => !!f);
    if (filesToMerge.length === 0) {
      toastNotify({ title: "Error", message: "Please upload at least the Invoice Copy", type: "error" });
      return;
    }

    try {
      setLoading(true);
      const mergedBytes = await mergeFiles(filesToMerge);
      const base64Content = arrayBufferToBase64(mergedBytes);

      const CHUNK_SIZE = 1.5 * 1024 * 1024;
      const totalChunks = Math.ceil(base64Content.length / CHUNK_SIZE);
      const uploadId = `upload_${documentId}_${Date.now()}`;
      let uploadedFileId = null;

      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, base64Content.length);
        const chunk = base64Content.slice(start, end);

        const chunkResponse: any = await appRequest("document", "uploadChunk", {
          uploadId,
          chunkIndex: i,
          totalChunks,
          chunkData: chunk,
          filename: `merged_${documentId}.pdf`,
          contentType: "application/pdf"
        });

        if (chunkResponse?.status === "success" && chunkResponse?.fileId) {
          uploadedFileId = chunkResponse.fileId;
        } else if (chunkResponse?.status === "error") {
          throw new Error(chunkResponse.message || "Failed to upload chunk");
        }
      }

      const response: any = await appRequest("document", "upload/foreign-process-docs", {
        documentId: documentId.trim(),
        fileId: uploadedFileId
      });

      if (response?.status === "success") {
        toastNotify({ title: "Success", message: "Documents merged and uploaded successfully", type: "success" });
        setFileSlots({ invoice: null, agreement: null, tax: null, other: null });
        setPreviews({ invoice: null, agreement: null, tax: null, other: null });
        setDocumentId("");
        setDocFound(false);
      } else {
        toastNotify({ title: "Error", message: response?.message || "Final attachment failed", type: "error" });
      }
    } catch (error: any) {
      toastNotify({ title: "Error", message: error.message || "Internal error during merge/upload", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      p={8}
      minH="100vh"
      bgGradient={pageBg}
    >
      <Box>
        <Stack spacing={8}>
          {/* Header Section */}
          <MotionBox
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <VStack align="start" spacing={1}>
              <Heading size="lg" bgGradient="linear(to-r, teal.400, blue.500)" bgClip="text" fontWeight="extrabold">
                Foreign Document Upload
              </Heading>
              <Text color="gray.500" fontSize="md">Merge and upload multiple documents for verification</Text>
            </VStack>
          </MotionBox>

          {/* Search Card */}
          <MotionBox
            bg={bgColor}
            p={8}
            borderRadius="2xl"
            {...glassStyles}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
          >
            <VStack spacing={4} align="stretch">
              <HStack justify="space-between">
                <Text fontWeight="bold" fontSize="lg" color="teal.500">Step 1: Locate Document</Text>
                {docFound && <Icon as={CheckCircleIcon} color="green.400" boxSize={6} />}
              </HStack>
              <InputGroup size="lg">
                <Input
                  variant="filled"
                  placeholder="Enter Document ID (e.g. 0afe0c79...)"
                  value={documentId}
                  onChange={(e) => setDocumentId(e.target.value)}
                  borderRadius="xl"
                  _focus={{ borderColor: "teal.400", bg: inputFocusBg }}
                  fontWeight="medium"
                />
                <InputRightElement width="6rem">
                  <Button
                    h="2.5rem"
                    size="sm"
                    colorScheme="teal"
                    onClick={handleSearch}
                    isLoading={isSearching}
                    leftIcon={<SearchIcon />}
                    borderRadius="lg"
                    px={6}
                    boxShadow="md"
                    _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
                    _active={{ transform: "translateY(0)" }}
                  >
                    Search
                  </Button>
                </InputRightElement>
              </InputGroup>
            </VStack>
          </MotionBox>

          {/* Upload Grid Card */}
          <AnimatePresence>
            {docFound && (
              <MotionBox
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                bg={bgColor}
                p={8}
                borderRadius="2xl"
                {...glassStyles}
              >
                <VStack spacing={8} align="stretch">
                  <Text fontWeight="bold" fontSize="lg" color="teal.600">Step 2: Attach Documents</Text>

                  <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={8}>
                    {DOC_SLOTS.map((slot) => (
                      <GridItem key={slot.key}>
                        <FormControl isRequired={slot.required}>
                          <FormLabel fontSize="sm" fontWeight="semibold" mb={3} color="gray.600">
                            {slot.label} {slot.required && <Text as="span" color="red.400">*</Text>}
                          </FormLabel>
                          <Box
                            height="120px"
                            border="2px dashed"
                            borderColor={fileSlots[slot.key] ? "teal.400" : "gray.300"}
                            borderRadius="xl"
                            position="relative"
                            bg={fileSlots[slot.key] ? slotBg : "transparent"}
                            transition="all 0.2s"
                            _hover={{ borderColor: "teal.400", bg: slotHoverBg }}
                          >
                            <Input
                              type="file"
                              accept=".pdf,image/*"
                              display="none"
                              id={`file-input-${slot.key}`}
                              onChange={(e) => handleFileChange(e, slot.key)}
                            />
                            {fileSlots[slot.key] ? (
                              <Flex h="full" align="center" justify="center" px={4}>
                                <VStack spacing={1}>
                                  <Icon as={AttachmentIcon} color="teal.500" boxSize={5} />
                                  <Text fontSize="xs" fontWeight="medium" textAlign="center" noOfLines={1} maxW="180px">
                                    {fileSlots[slot.key]?.name}
                                  </Text>
                                  <Button
                                    size="xs"
                                    variant="link"
                                    colorScheme="red"
                                    onClick={() => removeFile(slot.key)}
                                    leftIcon={<CloseIcon fontSize="8px" />}
                                  >
                                    Remove
                                  </Button>
                                </VStack>
                              </Flex>
                            ) : (
                              <label htmlFor={`file-input-${slot.key}`} style={{ cursor: "pointer", width: "100%", height: "100%", display: "flex" }}>
                                <VStack h="full" w="full" align="center" justify="center" spacing={2}>
                                  <Icon as={slot.icon} color="gray.400" boxSize={6} />
                                  <Text fontSize="xs" color="gray.500">Tap to upload</Text>
                                </VStack>
                              </label>
                            )}
                          </Box>
                        </FormControl>
                      </GridItem>
                    ))}
                  </Grid>

                  <Divider />

                  <Button
                    size="lg"
                    h="60px"
                    bgGradient="linear(to-r, teal.400, teal.600, blue.500)"
                    color="white"
                    isLoading={loading}
                    loadingText="Merging & Processing Chunks..."
                    onClick={handleSubmit}
                    borderRadius="xl"
                    fontSize="md"
                    fontWeight="bold"
                    _hover={{
                      bgGradient: "linear(to-r, teal.500, blue.600)",
                      transform: "translateY(-2px)",
                      boxShadow: "xl"
                    }}
                    _active={{ transform: "translateY(0)" }}
                  >
                    Generate and Upload Final Bundle
                  </Button>
                </VStack>
              </MotionBox>
            )}
          </AnimatePresence>
        </Stack>
      </Box>
    </Box>
  );
}
